import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useTranslate } from 'piral-translate';
import { useQuickNotesStore } from './quickNotes.store';
import { useConfirmDialogStore } from '../ConfirmDialog/ConfirmDialog';
import { XMarkIcon, TrashIconSolid } from '../Icons/Icons';
import './quick-notes.css';

// Note icon
const NoteIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
    />
  </svg>
);

// Plus icon
const PlusIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
);

// Expand icon (arrows pointing outward from corners)
const ExpandIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
    />
  </svg>
);

// Collapse icon (arrows pointing inward to center)
const CollapseIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 9V4.5M9 9H4.5M9 9 3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5 5.25 5.25"
    />
  </svg>
);

// Chevron left icon (for collapsing sidebar)
const ChevronLeftIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
  </svg>
);

// Chevron right icon (for expanding sidebar)
const ChevronRightIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
  </svg>
);

export const QuickNotes: React.FC = () => {
  const { isOpen, isDetached, notes, activeNoteId, close, toggleDetached, addNote, updateNote, deleteNote, setActiveNote } = useQuickNotesStore();
  const { open: openConfirm } = useConfirmDialogStore();

  const translate = useTranslate();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ isDragging: boolean; startX: number; startY: number; initialX: number; initialY: number }>({
    isDragging: false,
    startX: 0,
    startY: 0,
    initialX: 0,
    initialY: 0,
  });

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [size, setSize] = useState({ width: 900, height: 560 });
  const [isInitialized, setIsInitialized] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [opacity, setOpacity] = useState(100);
  const [isDarkMode, setIsDarkMode] = useState(() => document.documentElement.classList.contains('dark'));

  const activeNote = notes.find(n => n.id === activeNoteId);

  // Track dark mode changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Initialize position when detaching - center on screen
  useEffect(() => {
    if (isDetached && !isInitialized) {
      const centerX = (window.innerWidth - size.width) / 2;
      const centerY = (window.innerHeight - size.height) / 2;
      setPosition({ x: centerX, y: centerY });
      setIsInitialized(true);
    } else if (!isDetached) {
      setIsInitialized(false);
      // Reset size to default when snapping back
      setSize({ width: 900, height: 560 });
    }
  }, [isDetached, isInitialized, size.width, size.height]);

  // Drag handlers
  const handleDragStart = useCallback(
    (e: React.MouseEvent) => {
      if (!isDetached) return;
      e.preventDefault();
      dragRef.current = {
        isDragging: true,
        startX: e.clientX,
        startY: e.clientY,
        initialX: position.x,
        initialY: position.y,
      };
    },
    [isDetached, position],
  );

  useEffect(() => {
    if (!isDetached) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!dragRef.current.isDragging) return;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      setPosition({
        x: dragRef.current.initialX + dx,
        y: dragRef.current.initialY + dy,
      });
    };

    const handleMouseUp = () => {
      dragRef.current.isDragging = false;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDetached]);

  // Resize handler
  const handleResize = useCallback(
    (e: React.MouseEvent, direction: string) => {
      if (!isDetached) return;
      e.preventDefault();
      e.stopPropagation();

      const startX = e.clientX;
      const startY = e.clientY;
      const startWidth = size.width;
      const startHeight = size.height;
      const startPosX = position.x;
      const startPosY = position.y;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const dx = moveEvent.clientX - startX;
        const dy = moveEvent.clientY - startY;

        let newWidth = startWidth;
        let newHeight = startHeight;
        let newX = startPosX;
        let newY = startPosY;

        if (direction.includes('e')) newWidth = Math.max(600, startWidth + dx);
        if (direction.includes('w')) {
          newWidth = Math.max(600, startWidth - dx);
          newX = startPosX + (startWidth - newWidth);
        }
        if (direction.includes('s')) newHeight = Math.max(300, startHeight + dy);
        if (direction.includes('n')) {
          newHeight = Math.max(300, startHeight - dy);
          newY = startPosY + (startHeight - newHeight);
        }

        setSize({ width: newWidth, height: newHeight });
        setPosition({ x: newX, y: newY });
      };

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [isDetached, size, position],
  );

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, close]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget && !isDetached) close();
    },
    [close, isDetached],
  );

  const handleContentChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (activeNoteId) {
        updateNote(activeNoteId, e.target.value);
      }
    },
    [activeNoteId, updateNote],
  );

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getPreview = (content: string) => {
    const firstLine = content.split('\n')[0].trim();
    return firstLine || translate('quick-notes-empty');
  };

  if (!isOpen) return null;

  const modalStyle = isDetached
    ? ({
        position: 'fixed' as const,
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
        maxWidth: 'none',
        '--qn-opacity': opacity / 100,
        '--qn-blur': `${(opacity / 100) * 16}px`,
      } as React.CSSProperties)
    : {};

  return (
    <div className={`neo-quick-notes-backdrop ${isDetached ? 'detached' : ''}`} onClick={handleBackdropClick}>
      <div ref={modalRef} className={`neo-quick-notes-modal ${isDetached ? 'detached' : ''}`} style={modalStyle}>
        {/* Resize handles (only in detached mode) */}
        {isDetached && (
          <>
            <div className="neo-quick-notes-resize neo-quick-notes-resize-n" onMouseDown={e => handleResize(e, 'n')} />
            <div className="neo-quick-notes-resize neo-quick-notes-resize-s" onMouseDown={e => handleResize(e, 's')} />
            <div className="neo-quick-notes-resize neo-quick-notes-resize-e" onMouseDown={e => handleResize(e, 'e')} />
            <div className="neo-quick-notes-resize neo-quick-notes-resize-w" onMouseDown={e => handleResize(e, 'w')} />
            <div className="neo-quick-notes-resize neo-quick-notes-resize-ne" onMouseDown={e => handleResize(e, 'ne')} />
            <div className="neo-quick-notes-resize neo-quick-notes-resize-nw" onMouseDown={e => handleResize(e, 'nw')} />
            <div className="neo-quick-notes-resize neo-quick-notes-resize-se" onMouseDown={e => handleResize(e, 'se')} />
            <div className="neo-quick-notes-resize neo-quick-notes-resize-sw" onMouseDown={e => handleResize(e, 'sw')} />
          </>
        )}

        {/* Header */}
        <div className={`neo-quick-notes-header ${isDetached ? 'draggable' : ''}`} onMouseDown={handleDragStart}>
          <div className="neo-quick-notes-title">
            <NoteIcon className="neo-quick-notes-title-icon" />
            <span>{translate('quick-notes')}</span>
          </div>
          <div className="neo-quick-notes-header-actions">
            {isDetached && (
              <div className="neo-quick-notes-opacity-control" onMouseDown={e => e.stopPropagation()}>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={opacity}
                  onChange={e => setOpacity(Number(e.target.value))}
                  className="neo-quick-notes-opacity-slider"
                  title={`${translate('opacity')}: ${opacity}%`}
                />
              </div>
            )}
            <button type="button" className="neo-quick-notes-detach-btn" onClick={toggleDetached} title={isDetached ? 'Collapse' : 'Expand'}>
              {isDetached ? <ExpandIcon /> : <CollapseIcon />}
            </button>
            <button type="button" className="neo-quick-notes-add-btn" onClick={addNote} title={translate('quick-notes-new')}>
              <PlusIcon />
            </button>
            <button type="button" className="neo-quick-notes-close-btn" onClick={close}>
              <XMarkIcon />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="neo-quick-notes-content">
          {/* Notes list */}
          <div className={`neo-quick-notes-list ${isSidebarCollapsed ? 'collapsed' : ''}`}>
            {notes.length === 0 ? (
              <div className="neo-quick-notes-empty">
                <p>{translate('quick-notes-no-notes')}</p>
                <button type="button" onClick={addNote}>
                  {translate('quick-notes-create-first')}
                </button>
              </div>
            ) : (
              <>
                <div className="neo-quick-notes-section-header">{translate('notes')}</div>
                {notes.map(note => (
                  <div key={note.id} className={`neo-quick-notes-item ${note.id === activeNoteId ? 'active' : ''}`} onClick={() => setActiveNote(note.id)}>
                    <div className="neo-quick-notes-item-preview">{getPreview(note.content)}</div>
                    <div className="neo-quick-notes-item-date">{formatDate(note.updatedAt)}</div>
                    <button
                      type="button"
                      className="neo-quick-notes-item-delete"
                      onClick={e => {
                        e.stopPropagation();
                        openConfirm({
                          title: translate('quick-notes-delete'),
                          message: translate('quick-notes-delete-confirm'),
                          confirmLabel: translate('delete'),
                          onConfirm: () => deleteNote(note.id),
                        });
                      }}
                      title={translate('quick-notes-delete')}>
                      <TrashIconSolid />
                    </button>
                  </div>
                ))}
              </>
            )}
          </div>

          {/* Sidebar toggle button - outside sidebar so it stays visible */}
          <button
            type="button"
            className={`neo-quick-notes-sidebar-toggle ${isSidebarCollapsed ? 'collapsed' : ''}`}
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            title={isSidebarCollapsed ? translate('quick-notes-expand-sidebar') : translate('quick-notes-collapse-sidebar')}>
            {isSidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </button>

          {/* Editor */}
          <div
            className="neo-quick-notes-editor"
            style={
              isDetached
                ? {
                    background: isDarkMode
                      ? `rgba(30, 30, 30, ${opacity === 100 ? 0.85 : 0.1 + 0.5 * (opacity / 100)})`
                      : `rgba(255, 255, 255, ${opacity === 100 ? 0.6 : 0.5 + 0.45 * (opacity / 100)})`,
                    backdropFilter: opacity === 100 ? 'blur(16px)' : `blur(${(opacity / 100) * 16}px)`,
                  }
                : undefined
            }>
            {activeNote ? (
              <textarea ref={textareaRef} className="neo-quick-notes-textarea" value={activeNote.content} onChange={handleContentChange} />
            ) : (
              <div className="neo-quick-notes-no-selection">
                <NoteIcon className="neo-quick-notes-no-selection-icon" />
                <p>{translate('quick-notes-select')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
